import { useNavigate, Link } from 'react-router-dom';
import { useDemo, type DemoRole } from '@/contexts/DemoContext';
import type { SettingType } from '@/hooks/useFeatures.constants';
import { Button } from '@/components/ui/button';
import {
  Leaf, Stethoscope, Heart, ArrowLeft, Play,
  ShieldCheck, Trash2, CheckCircle2,
  Users, ArrowRight, Sparkles, Mic,
} from 'lucide-react';

const PRAXIS_FEATURES = [
  'Patientenakte & Kalender',
  'GNCP-Dokumentation mit manueller Freigabe',
  'Rechnungen, GKV-Abrechnung & Warteliste',
  'Nachrichten & Aufgaben',
  'Teletherapie und Sitzungsnotizen',
];

const DOKU_FEATURES = [
  'Transkription als Arbeitsgrundlage',
  'Zusammenfassung ohne Therapieempfehlung',
  'GNCP-Entwurf mit Quellenstellen',
  'Diagnose, Intervention und Monitoring bleiben manuell',
  'Fachliche Freigabe vor Abschluss',
];

const PORTAL_FEATURES = [
  'Ernährungstagebuch',
  'Nachrichten & Aufgaben vom Therapeuten',
  'Dokumente & Befunde',
  'Stimmungs-Check-in & Reflexion',
  'Eigene Portalübersicht',
];

const DEMO_PATIENTS_PREVIEW = [
  { name: 'Anna Müller', focus: 'Gewichtsmanagement' },
  { name: 'Thomas Weber', focus: 'Diabetesberatung' },
  { name: 'Lisa Schmidt', focus: 'Essverhalten' },
  { name: 'Klaus Hoffmann', focus: 'Aufbaukost' },
  { name: 'Sarah Becker', focus: 'Zöliakie-Beratung' },
  { name: 'Max Mustermann', focus: 'Prävention' },
];

const DemoLanding = () => {
  const navigate = useNavigate();
  const { startDemo } = useDemo();

  const handleStart = (role: DemoRole, settingType: SettingType = 'praxis') => {
    startDemo(role, settingType);
    if (role === 'therapeut') {
      navigate('/dashboard');
    } else {
      navigate('/portal');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50/60 via-background to-amber-50/30">
      <div className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-primary" />
            <span className="font-bold text-foreground tracking-tight">Icarus D&D</span>
            <span className="text-xs text-muted-foreground ml-1">by Icarus Health</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-muted-foreground text-xs">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Zurück zur Startseite
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-10">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-full">
            <Play className="w-3 h-3" />
            Interaktive Demo - keine Registrierung nötig
          </div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">
            Icarus D&D live erleben
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Erkunden Sie den öffentlichen Praxis-MVP mit fiktiven Daten: Schaltzentrale,
            Patientenportal und KI-Dokumentationsentwurf ohne medizinische Vorentscheidung.
          </p>
        </div>

        <div className="bg-card rounded-2xl border border-border p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Demo-Patienten inklusive</span>
            <span className="ml-auto text-xs text-muted-foreground">Alle Daten fiktiv</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {DEMO_PATIENTS_PREVIEW.map((p) => (
              <div key={p.name} className="flex items-center gap-2 bg-secondary/40 rounded-lg px-3 py-2">
                <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-bold text-primary">
                    {p.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{p.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{p.focus}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => handleStart('therapeut', 'praxis')}
            className="group bg-card rounded-2xl border-2 border-border p-6 text-left hover:border-[hsl(var(--primary))] hover:shadow-lg transition-all duration-300 flex flex-col"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
              <Stethoscope className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-base font-bold text-foreground">Praxis-Schaltzentrale</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-4 leading-relaxed">
              Ernährungstherapie und Verwaltung in der ambulanten Praxis.
            </p>
            <ul className="space-y-1.5 mb-5 flex-1">
              {PRAXIS_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-px" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-primary text-sm font-semibold mt-auto">
              Praxis starten
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => handleStart('therapeut', 'praxis')}
            className="group bg-card rounded-2xl border-2 border-border p-6 text-left hover:border-blue-500 hover:shadow-lg transition-all duration-300 flex flex-col"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
              <Mic className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-base font-bold text-foreground">Auto-Doku erleben</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-4 leading-relaxed">
              Voice-in, Transkript und prüfbarer Dokumentationsentwurf.
            </p>
            <ul className="space-y-1.5 mb-5 flex-1">
              {DOKU_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-px" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-blue-600 text-sm font-semibold mt-auto">
              Doku starten
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => handleStart('patient')}
            className="group bg-card rounded-2xl border-2 border-border p-6 text-left hover:border-green-600 hover:shadow-lg transition-all duration-300 flex flex-col"
          >
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
              <Heart className="w-6 h-6 text-green-700" />
            </div>
            <h2 className="text-base font-bold text-foreground">Patientenportal</h2>
            <p className="text-xs text-muted-foreground mt-1 mb-4 leading-relaxed">
              Die Patientenperspektive: Self-Service-Portal mit fiktiven Demo-Daten.
            </p>
            <ul className="space-y-1.5 mb-5 flex-1">
              {PORTAL_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-px" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-green-700 text-sm font-semibold mt-auto">
              Portal starten
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Öffentlicher MVP:</strong> Die Demo zeigt Praxisverwaltung, Portal,
            Teletherapie und Dokumentationsassistenz. Diagnose-, Therapie-, Risiko- und
            Priorisierungsentscheidungen werden nicht durch Icarus D&D getroffen.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-card rounded-lg px-3 py-2 border border-border">
            <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
            Keine Registrierung nötig
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-card rounded-lg px-3 py-2 border border-border">
            <Trash2 className="w-4 h-4 text-amber-600 shrink-0" />
            Alle Daten werden nach der Sitzung verworfen
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoLanding;
